/**
 * BUILDER preview → backend insert payload mapping.
 * <p>
 * BUILDER agent'ının ürettiği {@code structure.modules[]} preview yapısını
 * (LLM id'leri: m1, f1, ...) `POST /api/feature/add-groups-with-features`
 * endpoint'inin beklediği `AddGroupsAndFeaturesRequest` formatına çevirir.
 * <p>
 * Mapping kuralları:
 * <ul>
 *   <li><b>module.name → group.summary</b> (backend "summary" alanı NAME olarak
 *       kullanılır — proje konvansiyonu, kafa karıştırıcı ama mevcut.)</li>
 *   <li><b>module.summary[last].text → group.description</b> (Faz 1'de eklenen
 *       yeni GroupDto.description field'ı).</li>
 *   <li><b>module.color → group.properties.color</b> (frontend palette'ten
 *       atanan renk; factory DEFAULT_GROUP_COLOR'ı override eder).</li>
 *   <li><b>module.open_points → group.openPoints</b> ({title, summary}
 *       array; backend feature_open_point tablosuna persist eder).</li>
 *   <li><b>feature.name → feature.summary</b>, feature.summary[last].text →
 *       feature.description, vs.</li>
 *   <li>parentId backend tarafında set ediliyor (group.featureId üzerinden);
 *       biz göndermiyoruz.</li>
 *   <li><b>featureType: 'FEATURE'</b> hardcoded — subfeatures şu an ignore.</li>
 * </ul>
 * <p>
 * Pages (project_summary, problem, audience...) artık {@link builderToPagesPayload}
 * tarafından `POST /api/project-page/add-pages-bulk`'a ayrı çağrıda gönderiliyor.
 * agent-chat.store.ts `builder_insert` case'inde modules+features insert'inden
 * sonra fail-soft olarak çağrılır (pages fail olursa modules akışı bozulmaz).
 */

import type {AgentNs, FeatureNs} from '@shared/types';
// Faz 4.4: description'lar kaynağında Tiptap doc JSON'u olarak üretilir
// (BE guard'ı düz metni yine sarar ama o artık dış istemciler için güvenlik ağı).
// Doğrudan modül import'u (barrel değil): barrel, TiptapEditor bileşenini
// re-export ettiği için chunk'lar arası Rollup döngüsü kuruyordu.
import {textToTiptapDoc} from '@shared/components/tiptap-editor/content';

/**
 * BUILDER structure'ı backend payload'una çevirir.
 *
 * @param structure BUILDER {@code agentResponse.modules} taşıyan yapı
 * @param workspaceId workspace UUID
 * @param projectId project UUID
 * @returns `addGroupsWithFeatures` çağrısına gönderilecek body
 */
export function builderToFeaturePayload(
  structure: AgentNs.Structure | null,
  workspaceId: string,
  projectId: string,
): FeatureNs.AddGroupsAndFeaturesRequest {
  const groups: BuilderGroupOut[] = [];

  // Module listesi iki kaynaktan gelebilir: top-level structure.modules
  // veya sections[].modules. Sections varsa öncelik onun, yoksa top-level.
  const allModules: AgentNs.Module[] = collectModules(structure);

  for (const m of allModules) {
    // LLM bazen son bloğu `_` prefix'le "taslak" gibi emit ediyor
    // (_name, _id, _summary, _open_points). pick() her iki şekli de tolere eder.
    const moduleName = pick<string>(m, 'name');
    const moduleId = pick<string>(m, 'id');

    // İSİM ZORUNLU — backend Feature.summary NOT NULL. İsmi olmayan module'ü
    // tamamen atla (kötü/eksik LLM çıktısı DB'ye gitmesin → constraint violation
    // tüm transaction'ı rollback ettiriyordu).
    if (!moduleName || !moduleName.trim()) {
      // eslint-disable-next-line no-console
      console.warn('[builder_insert] skipping module without name', moduleId ?? m);
      continue;
    }

    const features: BuilderFeatureOut[] = [];
    for (const f of pick<AgentNs.Feature[]>(m, 'features') || []) {
      const featureName = pick<string>(f, 'name');
      const featureId = pick<string>(f, 'id');
      if (!featureName || !featureName.trim()) {
        // eslint-disable-next-line no-console
        console.warn('[builder_insert] skipping feature without name', featureId ?? f);
        continue;
      }
      features.push({
        id: featureId,
        featureType: 'FEATURE',
        summary: featureName,
        description: textToTiptapDoc(latestText(pick(f, 'summary'))),
        openPoints: normalizeOpenPoints(pick(f, 'open_points')),
      });
    }

    groups.push({
      id: moduleId,                                   // preview id — backend yok sayar; id mapping için tutuyoruz
      summary: moduleName,                            // → Feature.summary (NAME)
      description: textToTiptapDoc(latestText(pick(m, 'summary'))), // → Feature.description (Tiptap doc JSON)
      properties: m.color ? {color: m.color} : undefined,
      openPoints: normalizeOpenPoints(pick(m, 'open_points')),
      features,
    });
  }

  return {
    workspaceId,
    projectId,
    groups,
  };
}

/**
 * BUILDER structure'ının {@code pages[]} array'ini backend
 * `POST /api/project-page/add-pages-bulk` endpoint'inin beklediği
 * `UpdateProjectPageRequest` formatına çevirir.
 * <p>
 * Mapping kuralları:
 * <ul>
 *   <li><b>page.name → projectPage.summary</b> (Page entity'sinde summary alanı NAME).</li>
 *   <li><b>page.summary[last].text → projectPage.description</b>.</li>
 *   <li><b>isSummary: false</b> (Sample Page hariç hiçbiri "summary page" değil).</li>
 *   <li>orderNumber gönderilmez — backend max+1'den otomatik atar.</li>
 * </ul>
 * <p>
 * İsmi olmayan page'ler atlanır (constraint violation engelleme — modules ile aynı pattern).
 */
const BACKEND_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function builderToPagesPayload(
  structure: AgentNs.Structure | null,
  workspaceId: string,
  projectId: string,
): FeatureNs.UpdateProjectPageRequest {
  const pages = (structure?.pages ?? [])
    // A page that already exists in the project arrives in the merged structure under its backend
    // UUID. Only draft pages are created; re-sending an existing one would duplicate it.
    .filter((p) => !BACKEND_ID.test(String(pick<string>(p, 'id') ?? '')))
    .map((p) => {
      const name = pick<string>(p, 'name');
      if (!name || !name.trim()) {
        // eslint-disable-next-line no-console
        console.warn('[builder_insert] skipping page without name', p);
        return null;
      }
      return {
        summary: name,
        description: textToTiptapDoc(latestText(pick(p, 'summary'))),
        isSummary: false,
      } as FeatureNs.ProjectPage;
    })
    .filter((p): p is FeatureNs.ProjectPage => p !== null);

  return {
    workspaceId,
    projectId,
    projectPageList: pages,
  } as FeatureNs.UpdateProjectPageRequest;
}

/**
 * Bir objeden hem normal hem underscore-prefixed alanı dener.
 * LLM bazen {@code name} yerine {@code _name} emit ediyor (uzun çıktı
 * "taslak" işareti). Önce normal alan, yoksa underscore'lu.
 */
function pick<T = unknown>(obj: unknown, key: string): T | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const rec = obj as Record<string, unknown>;
  const normal = rec[key];
  if (normal !== undefined && normal !== null) return normal as T;
  const underscored = rec[`_${key}`];
  if (underscored !== undefined && underscored !== null) return underscored as T;
  return undefined;
}

/**
 * Backend `addGroupsWithFeatures` cevabından preview id ↔ backend UUID
 * mapping çıkartır. Backend Feature DTO'larında {@code id} = backend UUID,
 * order yapısı insert sırasıyla aynı (önce her group, sonra altındaki
 * feature'lar — FeatureService.java:362-381).
 * <p>
 * <b>Önemli:</b> Ham structure yerine {@link builderToFeaturePayload}'ın
 * ürettiği temiz {@code groups}'u alır — böylece helper'ın skip ettiği
 * (isimsiz/bozuk) entry'ler mapping'de de yok ve backend insert sırasıyla
 * birebir hizalı kalır.
 *
 * @param groups builderToFeaturePayload çıktısının `groups` dizisi
 * @param insertedFeatures backend addGroupsWithFeatures cevabı
 * @returns `[{previewId, backendId, kind}]`
 */
export function buildIdMapping(
  groups: BuilderGroupOut[],
  insertedFeatures: FeatureNs.Feature[],
): IdMappingEntry[] {
  const mapping: IdMappingEntry[] = [];
  if (!groups.length || !insertedFeatures.length) return mapping;

  let cursor = 0;
  for (const g of groups) {
    // Sıradaki backend entry GROUP olmalı.
    while (cursor < insertedFeatures.length
           && String(insertedFeatures[cursor]?.featureType).toUpperCase() !== 'GROUP') {
      cursor++;
    }
    const groupDto = insertedFeatures[cursor];
    if (!groupDto) break;
    if (g.id) mapping.push({previewId: g.id, backendId: groupDto.id, kind: 'module'});
    cursor++;

    // Sonraki feature'lar — bu group altındaki feature'lar (parentId = group.id).
    for (const f of g.features) {
      while (cursor < insertedFeatures.length) {
        const candidate = insertedFeatures[cursor];
        if (!candidate) break;
        const ftype = String(candidate.featureType).toUpperCase();
        if (ftype !== 'GROUP' && (candidate as any).parentId === groupDto.id) {
          if (f.id) mapping.push({previewId: f.id, backendId: candidate.id, kind: 'feature'});
          cursor++;
          break;
        }
        cursor++;
      }
    }
  }
  return mapping;
}

/**
 * Tam Yol 1 — commit response'undaki preview→backend id eşlemesini lokal
 * structure'a uygular (in-place). Backend delta-commit yeni yaratılan modül/
 * feature'lara UUID atar ve {@code idMapping} döner; bununla {@code currentStructure}
 * preview id'lerini günceller ki sonraki diff/resolve backend UUID üzerinden çalışsın.
 */
export function applyIdMapping(
  structure: AgentNs.Structure | null,
  idMapping: Array<{previewId: string; backendId: string}> | undefined,
): void {
  if (!structure || !idMapping || idMapping.length === 0) return;
  const byPreview = new Map<string, string>();
  for (const e of idMapping) {
    if (e.previewId && e.backendId) byPreview.set(e.previewId, e.backendId);
  }
  for (const m of collectModules(structure)) {
    const mid = pick<string>(m, 'id');
    if (mid && byPreview.has(mid)) {
      (m as Record<string, unknown>).id = byPreview.get(mid);
    }
    for (const f of pick<AgentNs.Feature[]>(m, 'features') || []) {
      const fid = pick<string>(f, 'id');
      if (fid && byPreview.has(fid)) {
        (f as Record<string, unknown>).id = byPreview.get(fid);
      }
    }
  }
}

// =====================================================================
//  Internal types
// =====================================================================

interface BuilderFeatureOut {
  id?: string;
  featureType: 'FEATURE' | 'GROUP' | 'SUB_FEATURE';
  summary: string;
  description?: string;
  openPoints?: Array<{title?: string; summary?: string}>;
}

interface BuilderGroupOut {
  id?: string;
  summary: string;
  description?: string;
  properties?: Record<string, unknown>;
  openPoints?: Array<{title?: string; summary?: string}>;
  features: BuilderFeatureOut[];
}

export interface IdMappingEntry {
  previewId: string;
  backendId: string;
  kind: 'module' | 'feature';
}

// =====================================================================
//  Helpers
// =====================================================================

/**
 * Structure.modules veya structure.sections[].modules'ün birleşik listesi.
 * BUILDER bazı modlarda section'lar üretebiliyor; section yoksa düz modules.
 */
function collectModules(structure: AgentNs.Structure | null): AgentNs.Module[] {
  if (!structure) return [];
  if (structure.sections && structure.sections.length > 0) {
    return structure.sections.flatMap(s => s.modules || []);
  }
  return structure.modules || [];
}

/**
 * BUILDER {@code summary: SummaryEntry[]} array'inin son text'ini döner.
 * Eski şema (string) ve yeni şema (array of {text, created_at}) toleranslı.
 * <p>
 * Boş veya null → undefined (caller field'ı emit etmez).
 */
function latestText(summary: unknown): string | undefined {
  if (!summary) return undefined;
  if (typeof summary === 'string') return summary.trim() || undefined;
  if (Array.isArray(summary)) {
    // History is append-only: match the card and backend commit's latest entry.
    return extractText(summary[summary.length - 1]);
  }
  return extractText(summary);
}

/**
 * Tek bir summary entry'sinden text çeker. Tolere edilen şekiller:
 * <ul>
 *   <li>düz string</li>
 *   <li>{@code { text: "..." }} (normal)</li>
 *   <li>{@code { _text: "..." }} (LLM taslak prefix)</li>
 *   <li>{@code { text: [{ _text: "..." }] }} (bozuk nested array — m5 örneği)</li>
 * </ul>
 */
function extractText(item: unknown): string | undefined {
  if (typeof item === 'string') return item.trim() || undefined;
  if (item && typeof item === 'object') {
    const rec = item as Record<string, unknown>;
    const candidate = rec.text ?? rec._text;
    if (typeof candidate === 'string') return candidate.trim() || undefined;
    if (Array.isArray(candidate)) {
      for (const c of candidate) {
        const t = extractText(c);
        if (t) return t;
      }
    }
  }
  return undefined;
}

/**
 * open_points'i {title, summary} object array'e normalize eder.
 * <p>
 * LLM şemaları:
 * <ul>
 *   <li>Object: {@code {title: "...", summary: "..."}} → olduğu gibi.</li>
 *   <li>String (eski): {@code "Tech stack decision"} → {@code {title: "..."}}.</li>
 * </ul>
 * Boş array veya null → undefined (caller field'ı emit etmez).
 */
function normalizeOpenPoints(
  openPoints: unknown,
): Array<{title?: string; summary?: string}> | undefined {
  if (!Array.isArray(openPoints) || openPoints.length === 0) return undefined;
  const out: Array<{title?: string; summary?: string}> = [];
  for (const op of openPoints) {
    if (typeof op === 'string') {
      if (op.trim()) out.push({title: op});
    } else if (op && typeof op === 'object') {
      const title = typeof (op as any).title === 'string' ? (op as any).title : undefined;
      const summary = typeof (op as any).summary === 'string' ? (op as any).summary : undefined;
      if (title || summary) out.push({title, summary});
    }
  }
  return out.length > 0 ? out : undefined;
}
